import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DashboardHome from "./DashboardHome";
import { useAuth } from "../../context/AuthContext";
import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import ConfirmModal from "./ConfirmModal";

function NavItem({ to, iconClass, label, active, isCollapsed }) {
  return (
    <Link
      to={to}
      title={isCollapsed ? label : ""}
      className={`nav-item ${active ? "active-link" : ""}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isCollapsed ? "center" : "flex-start",
        gap: isCollapsed ? "0" : "0.75rem",
        padding: "0.65rem 0.9rem",
        borderRadius: "0.6rem",
        fontWeight: 500,
        color: active ? "#2B6CB0" : "#4A5568",
        backgroundColor: active ? "#E2E8F0" : "transparent",
        textDecoration: "none",
        border: "1px solid transparent",
        transition: "all .2s ease",
        width: "100%",
        boxSizing: "border-box"
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "#90CDF4";
          e.currentTarget.style.color = "#1A365D";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "#4A5568";
        }
      }}
    >
      <i
        className={iconClass}
        style={{ fontSize: 18, color: "#2B6CB0", width: 24, textAlign: "center" }}
      />
      {!isCollapsed && <span>{label}</span>}
    </Link>
  );
}

function Section({ title, children, isCollapsed }) {
  if (isCollapsed) return <div style={{ display: "grid", gap: "0.6rem", marginBottom: "1rem" }}>{children}</div>;

  return (
    <li style={{ marginBottom: "1rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          padding: "0.6rem 0.9rem",
          borderRadius: "0.6rem",
          fontWeight: 600,
          backgroundColor: "#C6F6D5",
          color: "#065F46",
          border: "1px solid #A7F3D0",
          marginBottom: "0.5rem",
          fontSize: "0.85rem",
          textTransform: "uppercase",
          letterSpacing: "0.025em"
        }}
      >
        <i
          className="fas fa-folder-open"
          style={{ fontSize: 16, color: "#059669", width: 24, textAlign: "center" }}
        />
        <span>{title}</span>
      </div>
      <div style={{ display: "grid", gap: "0.4rem" }}>{children}</div>
    </li>
  );
}

export default function AdminDashboard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    logout();
    navigate("/login");
  };

  const sidebarWidth = isCollapsed ? 80 : 260;

  return (
    <div className="flex h-screen bg-gray-50 transition-all duration-300">
      {/* Sidebar */}
      <aside
        className="admin-sidebar"
        style={{
          width: sidebarWidth,
          backgroundColor: "#BEE3F8",
          color: "#1F2937",
          height: "100vh",
          padding: isCollapsed ? "1.5rem 0.5rem" : "1.5rem 1rem",
          boxShadow: "4px 0px 8px rgba(0,0,0,0.05)",
          position: "fixed",
          display: "flex",
          flexDirection: "column",
          zIndex: 10,
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            position: "absolute",
            right: -12,
            top: 32,
            width: 24,
            height: 24,
            backgroundColor: "#2B6CB0",
            color: "white",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid white",
            zIndex: 20,
            transition: "transform 0.3s"
          }}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        {/* Logo/Header Area */}
        <div
          style={{
            padding: "0.5rem 1rem",
            marginBottom: "1.5rem",
            borderBottom: "2px solid #90CDF4",
            textAlign: "center",
            overflow: "hidden"
          }}
        >
          <h2
            style={{
              fontWeight: 800,
              fontSize: "1.25rem",
              color: "#2C5282",
              margin: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              whiteSpace: "nowrap"
            }}
          >
            <span className="text-2xl">🌍</span>
            {!isCollapsed && "TravelAura"}
          </h2>
          {!isCollapsed && (
            <p className="text-[10px] text-sky-700 font-bold uppercase tracking-widest mt-1">Management Portal</p>
          )}
        </div>

        <nav style={{ flex: 1, overflowY: "auto", paddingRight: isCollapsed ? "0" : "0.5rem" }}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {/* Dashboard Home */}
            <li style={{ marginBottom: "1.5rem" }}>
              <NavItem
                to="/admindash"
                iconClass="fas fa-th-large"
                label="Overview"
                active={location.pathname === "/admindash"}
                isCollapsed={isCollapsed}
              />
            </li>

            {/* === Sections === */}
            <Section title="Main Content" isCollapsed={isCollapsed}>
              <NavItem
                to="/adddestination"
                iconClass="fas fa-plus-circle"
                label="Add Destination"
                active={location.pathname === "/adddestination"}
                isCollapsed={isCollapsed}
              />
              <NavItem
                to="/destinationview"
                iconClass="fas fa-map-marked-alt"
                label="View List"
                active={location.pathname === "/destinationview"}
                isCollapsed={isCollapsed}
              />
            </Section>

            <Section title="Tour Packages" isCollapsed={isCollapsed}>
              <NavItem
                to="/inserttravelpackage"
                iconClass="fas fa-luggage-cart"
                label="Create Package"
                active={location.pathname === "/inserttravelpackage"}
                isCollapsed={isCollapsed}
              />
              <NavItem
                to="/travelpackages"
                iconClass="fas fa-route"
                label="Manage Tours"
                active={location.pathname === "/travelpackages"}
                isCollapsed={isCollapsed}
              />
            </Section>

            <Section title="Accommodations" isCollapsed={isCollapsed}>
              <NavItem
                to="/insertaccommodation"
                iconClass="fas fa-hotel"
                label="Add Property"
                active={location.pathname === "/insertaccommodation"}
                isCollapsed={isCollapsed}
              />
              <NavItem
                to="/accommodation"
                iconClass="fas fa-bed"
                label="Manage Stays"
                active={location.pathname === "/accommodation"}
                isCollapsed={isCollapsed}
              />
            </Section>

            <Section title="Blogs & Reviews" isCollapsed={isCollapsed}>
              <NavItem
                to="/addblog"
                iconClass="fas fa-pen-nib"
                label="Write Blog"
                active={location.pathname === "/addblog"}
                isCollapsed={isCollapsed}
              />
              <NavItem
                to="/view"
                iconClass="fas fa-comments"
                label="Manage Blogs"
                active={location.pathname === "/view"}
                isCollapsed={isCollapsed}
              />
              <NavItem
                to="/admin/manage-reviews"
                iconClass="fas fa-star"
                label="Manage Reviews"
                active={location.pathname === "/admin/manage-reviews"}
                isCollapsed={isCollapsed}
              />
            </Section>

            <Section title="User Management" isCollapsed={isCollapsed}>
              <NavItem
                to="/admin/manage-users"
                iconClass="fas fa-users-cog"
                label="Manage Users"
                active={location.pathname === "/admin/manage-users"}
                isCollapsed={isCollapsed}
              />
            </Section>

            <Section title="System" isCollapsed={isCollapsed}>
              <NavItem
                to="/admin/events"
                iconClass="fas fa-calendar-star"
                label="Events Flow"
                active={location.pathname === "/admin/events"}
                isCollapsed={isCollapsed}
              />
              <NavItem
                to="/"
                iconClass="fas fa-external-link-alt"
                label="Main Website"
                isCollapsed={isCollapsed}
              />
            </Section>
          </ul>
        </nav>

        {/* Logout Button */}
        <div style={{ marginTop: "1rem", padding: "0 0.5rem" }}>
          <button
            onClick={handleLogout}
            title={isCollapsed ? "Logout" : ""}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isCollapsed ? "center" : "flex-start",
              gap: isCollapsed ? "0" : "0.75rem",
              padding: "0.75rem 0.9rem",
              borderRadius: "0.6rem",
              width: "100%",
              border: "none",
              backgroundColor: "#FEB2B2",
              color: "#9B2C2C",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#FC8181")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#FEB2B2")}
          >
            <LogOut size={18} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>

        {/* Sidebar Footer */}
        {!isCollapsed && (
          <div
            style={{
              padding: "1rem",
              marginTop: "auto",
              borderTop: "2px solid #90CDF4",
              textAlign: "center",
              fontSize: "0.75rem",
              color: "#2C5282",
              fontWeight: "bold"
            }}
          >
            <p style={{ margin: 0 }}>© 2026 TRAVELAURA ADMIN</p>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main
        style={{
          marginLeft: sidebarWidth,
          padding: "2.5rem",
          width: `calc(100% - ${sidebarWidth}px)`,
          minHeight: "100vh",
          backgroundColor: "#F7FAFC",
          overflowY: "auto",
          boxSizing: "border-box",
          transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        {children ? (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "1rem",
              padding: "2rem",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              border: "1px solid #E2E8F0"
            }}
          >
            {children}
          </div>
        ) : (
          <DashboardHome />
        )}
      </main>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={confirmLogout}
        title="Logout Confirmation"
        message="Are you sure you want to logout from the TravelAura Management Portal? Your current session will be ended."
        confirmText="Logout"
      />
    </div>
  );
}
